// Shared types and display helpers for the App Fleet page.
//
// The RN app ships OTAs through EAS Update with FINGERPRINT runtime versions:
// an update only reaches binaries whose native fingerprint matches the one it
// was published for. So a device's version state is per platform + channel +
// runtime, and the SERVER decides it (CITS-Server src/utils/appFleet.js,
// returned as `device.status`). Nothing here recomputes it — the old
// store-version + OTA-counter arithmetic said nothing about which updates a
// phone can actually receive.

export type Platform = 'ios' | 'android'

export type StatusKey = 'latest' | 'behind' | 'embedded' | 'no_updates' | 'unrecorded' | 'store' | 'unknown'

export interface DeviceStatus {
  key: StatusKey
  label: string
  detail: string
  /** Releases newer than the one running, within the device's runtime. */
  behind: number
  /** app_releases.id the device runs, when recorded. */
  release_id: number | null
  /** Newest release for its platform + channel + runtime. */
  latest_release_id: number | null
  channel: string
  runtime: string | null
  /** true = built-in bundle, false = downloaded update, null = app too old to say. */
  embedded: boolean | null
}

export interface Device {
  id: string
  company_code: string
  company_name: string | null
  employee_id: string | null
  employee_name: string | null
  platform: Platform
  os_version?: string | null
  device_model?: string | null
  app_version: string | null
  native_build?: string | null
  /** The shared OTA counter the running bundle carries. Display only. */
  ota_build: number | null
  ota_version: string | null
  runtime_version: string | null
  update_id: string | null
  is_embedded?: number | boolean | null
  update_created_at?: string | null
  channel: string | null
  last_seen_at: string
  created_at: string
  retired_at?: string | null
  superseded_by?: string | null
  retired_reason?: string | null
  /** Unseen 30+ days: kept, not counted. */
  inactive: boolean
  status: DeviceStatus
}

export interface OtaRelease {
  id: number | null
  platform: Platform
  app_version: string
  ota_build: number | null
  ota_version: string
  channel: string
  message: string | null
  git_commit: string | null
  published_at: string
  /** Null for xprem-era releases, whose runtime was the app version. */
  runtime_version: string | null
  eas_update_id: string | null
  eas_group_id: string | null
  update_level: 'normal' | 'soon' | 'critical' | null
  source: 'recorded' | 'reported'
}

export interface NativeBuild {
  platform: Platform
  store_version: string
  native_build: string
  runtime_version: string
  channel: string | null
  first_seen_at: string
  last_seen_at: string
  source?: string
  is_partial: boolean
  label?: string | null
  notes?: string | null
  derived?: boolean
  active_devices: number
  devices: number
  release_count: number
  latest_release: {
    id: number | null
    ota_build?: number | null
    ota_version: string | null
    channel: string
    published_at: string
    eas_update_id: string | null
    update_level: string | null
  } | null
}

export interface HealthItem {
  key: string
  tone: 'warn' | 'info' | 'ok'
  count: number
  title: string
  explanation: string
  fix: string
  update_ids?: string[]
  runtimes?: Array<{ platform: Platform; runtime: string }>
  releases?: Array<{ id: number | null; platform: Platform; ota_build?: number | null; ota_version: string | null; runtime_version: string; channel: string }>
}

export interface HistoryEntry {
  device_id?: string
  app_version: string | null
  native_build: string | null
  ota_build: number | null
  ota_version: string | null
  runtime_version?: string | null
  update_id: string | null
  is_embedded?: number | boolean | null
  channel: string | null
  seen_at: string
}

export interface StorePolicy {
  enabled: boolean
  min: { ios?: string | null; android?: string | null }
}

export function compareVersions(a: string | null | undefined, b: string | null | undefined): number {
  const pa = String(a ?? '').split('.').map((n) => Number(n) || 0)
  const pb = String(b ?? '').split('.').map((n) => Number(n) || 0)
  for (let i = 0; i < Math.max(pa.length, pb.length, 3); i++) {
    const x = pa[i] || 0
    const y = pb[i] || 0
    if (x !== y) return x < y ? -1 : 1
  }
  return 0
}

/** Channel names as the server compares them ("production" is the app's name for EAS "prod"). */
export function normChannel(c: string | null | undefined) {
  const s = String(c ?? '').trim().toLowerCase()
  if (!s || s === 'production') return 'prod'
  if (s === 'development') return 'dev'
  return s
}
export const channelOf = (d: { channel: string | null; status?: DeviceStatus }) => d.status?.channel || normChannel(d.channel)

/** Short fingerprint for display; the full one is copyable. */
export const shortFp = (rt: string | null | undefined) => (rt ? (rt.length > 12 ? rt.slice(0, 8) : rt) : '—')

/** True when the device runs the bundle built into its binary. */
export function onBuiltIn(d: Device) {
  return d.status.embedded === true || d.status.key === 'embedded' || d.status.key === 'no_updates'
}

/** "Store 4.0.28 (36)" */
export function storeLabel(d: { app_version: string | null; native_build?: string | null }) {
  return `Store ${d.app_version ?? '?'}${d.native_build ? ` (${d.native_build})` : ''}`
}

/** How an OTA is named: "Update 7", the counter on top of its store build (the
 *  app no longer derives a "4.1.7" style version). Rows recorded before that
 *  keep reading their stored version ("OTA 4.0.33"). */
export function updateName(r: { ota_build?: number | null; ota_version?: string | null }) {
  if (r.ota_build) return `Update ${r.ota_build}`
  return r.ota_version ? `OTA ${r.ota_version}` : 'Update'
}

/** "Update 7", or "built-in bundle". An embedded bundle's counter is from
 *  build time and would claim an update it never took. */
export function otaLabel(d: Device) {
  if (onBuiltIn(d)) return 'built-in bundle'
  if (d.ota_build || d.ota_version) return updateName(d)
  return 'Update unknown'
}

/** "Store 4.1.0 (40) · Update 7" */
export const versionLine = (d: Device) => `${storeLabel(d)} · ${otaLabel(d)}`

export const isActive = (d: Device, days: number | 'all') =>
  days === 'all' || Date.now() - new Date(d.last_seen_at).getTime() <= days * 86_400_000

export const daysSince = (iso: string) => (Date.now() - new Date(iso).getTime()) / 86_400_000

/** Devices that count: not retired, seen in the last 30 days. */
export const isCounted = (d: Device) => !d.retired_at && !d.inactive

export const STATUS_STYLE: Record<StatusKey, string> = {
  latest: 'bg-emerald-50 text-emerald-700',
  behind: 'bg-amber-50 text-amber-700',
  embedded: 'bg-amber-50 text-amber-700',
  no_updates: 'bg-slate-100 text-slate-700',
  unrecorded: 'bg-sky-50 text-sky-700',
  store: 'bg-red-50 text-red-700',
  unknown: 'bg-slate-100 text-slate-500',
}

export const SEGMENT_COLOR: Record<StatusKey, string> = {
  latest: 'bg-emerald-500',
  no_updates: 'bg-emerald-300',
  behind: 'bg-amber-400',
  embedded: 'bg-amber-300',
  unrecorded: 'bg-sky-400',
  store: 'bg-red-400',
  unknown: 'bg-slate-200',
}

export const STATUS_ORDER: Array<[StatusKey, string]> = [
  ['latest', 'Up to date'],
  ['no_updates', 'No updates for build'],
  ['behind', 'Update available'],
  ['embedded', 'Built-in, update available'],
  ['unrecorded', 'Not recorded'],
  ['store', 'Needs store update'],
  ['unknown', "Old app, can't check"],
]

export function timeAgo(iso: string) {
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000)
  if (min < 1) return 'just now'
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  return `${Math.floor(hr / 24)}d ago`
}

export function formatWhen(iso: string | null | undefined) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export const platformLabel = (p: Platform) => (p === 'ios' ? 'iOS' : 'Android')

export function deviceName(d: Device) {
  return d.employee_name || (d.employee_id ? `Employee ${d.employee_id}` : 'Nobody signed in')
}

/** "4.0.28 (36), 4.0.28 (37)" — the store builds that carry a fingerprint. */
export function buildsForRuntime(builds: NativeBuild[], platform: Platform, runtime: string | null) {
  if (!runtime) return []
  return builds.filter(
    (b) => b.platform === platform && (b.runtime_version === runtime || (b.is_partial && runtime.startsWith(b.runtime_version)))
  )
}
export const buildLabel = (b: { store_version: string; native_build: string }) =>
  `${b.store_version || '?'}${b.native_build ? ` (${b.native_build})` : ''}`
